import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { maintAnalyticsApi as api } from "../../../api/maintenance/analytics";
import type { UserDetail, UserSessionRow } from "../../../api/maintenance/analytics";
import SessionDetailDrawer, {
  SessionBasic, Field, Section, KPImini, num, money, kw, ts, statusPill,
} from "../sessions/SessionDetailDrawer";
import "../shared/maintenance-analytics.scss";

const UserDetailDrawer: React.FC<{ userId: number | null; onClose: () => void }> = ({ userId, onClose }) => {
  const [data, setData] = useState<UserDetail | "loading" | null>(null);
  const [sessId, setSessId] = useState<number | null>(null);
  const [sessBasic, setSessBasic] = useState<SessionBasic | null>(null);

  useEffect(() => {
    if (userId == null) return;
    setData("loading");
    api.userDetail(userId).then((r) => setData(r.data)).catch(() => setData(null));
  }, [userId]);

  if (userId == null) return null;
  const dd = data && data !== "loading" ? data : null;
  const u = dd?.user;
  const s = dd?.stats;

  const openSess = (row: UserSessionRow) => {
    setSessBasic({
      session_id: row.session_id, station: row.station, device: row.device, connector: row.connector_name,
      user_name: u?.user_name, phone: u?.phone, organization: u?.organization,
      consumed_kwh: row.consumed_kwh, charge_rate_kw: row.charge_rate_kw, total_cost: row.total_cost,
      duration_mins: row.duration_mins, status: row.status,
    });
    setSessId(row.session_id);
  };

  return (
    <>
      <div className="ma-drawer-bg" onClick={onClose}>
        <div className="ma-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="ma-drawer-h">
            <h3>User #{userId}{u?.status ? <> · <span className={`ma-pill ${u.status === "active" ? "ok" : "muted"}`}>{u.status}</span></> : ""}</h3>
            <button onClick={onClose}><X size={18} /></button>
          </div>
          <div className="ma-drawer-b ma">
            {data === "loading" && <p className="ma-loading">Loading user…</p>}
            {data === null && <p className="ma-empty">User not found</p>}
            {dd && u && s && (
              <>
                <div className="ma-kpis">
                  <KPImini label="Wallet" value={money(u.wallet_balance)} />
                  <KPImini label="Sessions" value={num(s.total_sessions)} />
                  <KPImini label="Energy" value={`${num(s.total_kwh, 1)} kWh`} />
                  <KPImini label="Spent" value={money(s.total_spent)} />
                </div>

                <Section title="Profile">
                  <Field label="Name" value={u.user_name} />
                  <Field label="Phone" value={u.phone} mono />
                  <Field label="Email" value={u.email} />
                  <Field label="Organization" value={u.organization} />
                  <Field label="Status" value={u.status} />
                  <Field label="Last session" value={ts(s.last_session)} />
                  <Field label="User GUID" value={u.user_guid} mono wide />
                </Section>

                {!!dd.vehicles.length && (
                  <Section title="Vehicles">
                    {dd.vehicles.map((v, i) => <Field key={i} label={v.vehicle_name || "Vehicle"} value={v.vehicle_model} />)}
                  </Section>
                )}

                <div className="ma-card">
                  <div className="ma-card-h"><h3>Recent sessions <span className="ma-hint">· click a row</span></h3></div>
                  <div className="ma-tablewrap">
                    <table className="ma-table">
                      <thead><tr><th className="num">#</th><th>Started</th><th>Station</th><th className="num">kWh</th><th className="num">Rate</th><th className="num">Cost</th><th>Status</th></tr></thead>
                      <tbody>
                        {dd.sessions.map((row) => (
                          <tr key={row.session_id} className="clickable" onClick={() => openSess(row)}>
                            <td className="num">{row.session_id}</td>
                            <td className="sub">{ts(row.session_started_on)}</td>
                            <td>{row.station || "—"}<div className="sub">{row.connector_name || ""}</div></td>
                            <td className="num">{num(row.consumed_kwh, 2)}</td>
                            <td className="num">{kw(row.charge_rate_kw)}</td>
                            <td className="num">{money(row.total_cost)}</td>
                            <td><span className={`ma-pill ${statusPill(row.status)}`}>{row.status || "—"}</span></td>
                          </tr>
                        ))}
                        {!dd.sessions.length && <tr><td colSpan={7} className="ma-empty">No sessions</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* nested: clicking a session opens the session drawer on top */}
      <SessionDetailDrawer sessionId={sessId} fallback={sessBasic} onClose={() => setSessId(null)} />
    </>
  );
};

export default UserDetailDrawer;
